import { useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { TerminationDocumentPolicy } from '../../api';
import {
  Form,
  FormError,
  PageHeader,
  Panel,
  SelectField,
  SubmitButton,
  TextField,
  fieldErrors,
  useNotifications,
} from '../../components';
import { PATHS, staffCompanyPath } from '../../routes/paths';
import { useCreateCompany } from '../queries';
import {
  ONBOARDING_COPY,
  TERMINATION_DOCUMENT_POLICY_HINTS,
  TERMINATION_DOCUMENT_POLICY_OPTIONS,
} from './onboarding';

/**
 * Registering a company: one screen, one form, one way back.
 *
 * Split out of the companies list so that the list answers "who is already a
 * customer?" and this answers "add one". The success path lands on the new
 * company's own page rather than clearing the fields in place, because what
 * somebody does next after registering a company -- contracts, people, a CNPJ
 * check -- happens there. Nothing here patches the cache by hand:
 * `useCreateCompany` invalidates, the list the page reads refetches, and what
 * appears is what the server said.
 *
 * There is no identifier field. The server derives the slug from the name
 * (and suffixes it when the name is taken), so the person registering does not
 * have to invent one; a name it cannot derive one from comes back as a refusal
 * on the name field.
 *
 * Leaving without saving is a link back to the list and creates nothing. There
 * is no draft to lose that the person did not just decide to lose.
 */
export function NewCompanyPage() {
  const create = useCreateCompany();
  const navigate = useNavigate();
  const { notify } = useNotifications();

  const [name, setName] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [terminationPolicy, setTerminationPolicy] = useState<TerminationDocumentPolicy>(
    TerminationDocumentPolicy.WARN,
  );

  const errors = fieldErrors(create.error);

  const submit = () => {
    // Sent as typed rather than stripped here: the API accepts every spelling
    // of one number and canonicalises it in one place, and a second
    // canonicaliser in the browser is a second one to keep right when the
    // alphabet changes -- which it just did.
    create.mutate(
      { name, cnpj: cnpj.trim(), termination_document_policy: terminationPolicy },
      {
        onSuccess: (company) => {
          notify('success', ONBOARDING_COPY.companyRegistered);
          void navigate(staffCompanyPath(company.id));
        },
      },
    );
  };

  return (
    <div className="page">
      <PageHeader
        title={ONBOARDING_COPY.registerCompanyTitle}
        description={ONBOARDING_COPY.registerCompanyDescription}
        actions={
          <Link className="button button--ghost" to={PATHS.staffCompanies}>
            {ONBOARDING_COPY.back}
          </Link>
        }
      />

      <Panel
        title={ONBOARDING_COPY.registerCompanyTitle}
        description={ONBOARDING_COPY.registerCompanyDescription}
      >
        <Form onSubmit={submit} aria-label={ONBOARDING_COPY.registerCompanyTitle}>
          <FormError error={create.error} />
          <TextField
            label={ONBOARDING_COPY.companyNameLabel}
            value={name}
            onChange={setName}
            hint={ONBOARDING_COPY.companyNameHint}
            required
            {...(errors['name'] !== undefined && { error: errors['name'] })}
          />
          <TextField
            label={ONBOARDING_COPY.companyCnpjLabel}
            value={cnpj}
            onChange={setCnpj}
            hint={ONBOARDING_COPY.companyCnpjHint}
            required
            {...(errors['cnpj'] !== undefined && { error: errors['cnpj'] })}
          />
          <SelectField
            label={ONBOARDING_COPY.terminationPolicyLabel}
            value={terminationPolicy}
            onChange={(chosen) => {
              if (chosen !== '') {
                setTerminationPolicy(chosen);
              }
            }}
            options={TERMINATION_DOCUMENT_POLICY_OPTIONS}
            hint={TERMINATION_DOCUMENT_POLICY_HINTS[terminationPolicy]}
            required
            {...(errors['termination_document_policy'] !== undefined && {
              error: errors['termination_document_policy'],
            })}
          />
          <div className="form__actions">
            <SubmitButton
              pending={create.isPending}
              pendingLabel={ONBOARDING_COPY.registeringCompany}
            >
              {ONBOARDING_COPY.registerCompany}
            </SubmitButton>
            <Link className="button button--ghost" to={PATHS.staffCompanies}>
              {ONBOARDING_COPY.cancel}
            </Link>
          </div>
        </Form>
      </Panel>
    </div>
  );
}
